import React from 'react';
import Cookies from 'js-cookie';
import ReCAPTCHA from 'react-google-recaptcha';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
   Box,
   Button,
   Card,
   Container,
   Flex,
   Heading,
   Skeleton,
   Text,
   TextField,
} from '@radix-ui/themes';
import { toast } from 'sonner';
import NavBar from 'Components/element/NavBar';
import Footer from 'Components/element/Footer';
import { FormField } from 'shared/components';
import { PasswordStrengthIndicator } from 'shared/components/password-strength-indicator';
import { CheckCircleOutlineIcon, VisibilityIcon, VisibilityOffIcon } from 'shared/icons';
import { useCandidateRegister } from './candidate-register.provider';

type CandidateRegisterViewProps = {
   captchaKey: string | null;
};

function CandidateRegisterView({ captchaKey }: CandidateRegisterViewProps) {
   const { t } = useTranslation('global');
   const navigate = useNavigate();
   const store = useCandidateRegister();

   const currentLanguage = Cookies.get('selectedLanguage') || 'ukr';
   const recaptchaLanguage = currentLanguage === 'ukr' ? 'uk' : currentLanguage;
   const [captchaReady, setCaptchaReady] = React.useState(false);
   const authToastOptions = React.useMemo(
      () => ({ classNames: { toast: 'auth-session-toast' } }),
      []
   );
   const authSuccessToastOptions = React.useMemo(
      () => ({
         ...authToastOptions,
         icon: <CheckCircleOutlineIcon style={{ color: '#93c47d', width: 24, height: 24 }} />,
      }),
      [authToastOptions]
   );
   const passwordStrengthTranslations = React.useMemo(
      () => ({
         strength: t('passwordStrength.strength', { defaultValue: 'Strength:' }),
         password_strength_1: t('passwordStrength.weak', { defaultValue: 'weak' }),
         password_strength_2: t('passwordStrength.medium', { defaultValue: 'medium' }),
         password_strength_3: t('passwordStrength.good', { defaultValue: 'good' }),
         password_strength_4: t('passwordStrength.strong', { defaultValue: 'strong' }),
      }),
      [t]
   );

   React.useEffect(() => {
      window.scrollTo(0, 0);

      const metaTitle = document.querySelector('title');
      const metaDescription = document.querySelector('meta[name="description"]');

      if (metaTitle) {
         metaTitle.textContent = t('jobseekerRegister.metaTitleValue');
      }

      if (metaDescription) {
         metaDescription.setAttribute('content', t('jobseekerRegister.metaDescriptionValue'));
      }

      if (store.hasActiveSession) {
         navigate('/');
         toast.warning(t('jobseekerRegister.alreadyLoggedIn'), authToastOptions);
      }
   }, [authToastOptions, navigate, store.hasActiveSession, t]);

   const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      await store.submit({
         onSuccess: () => {
            toast.success(t('jobseekerRegister.creationSuccessMessage'), authSuccessToastOptions);
            navigate('/auth/candidate-login');
         },
         onSmsRequired: (contact) => {
            window.localStorage.setItem('comfirmedPhone', contact);
            navigate('/sms-confirmed');
         },
         onError: (message) => {
            toast.error(message || t('jobseekerRegister.failedMessage'), authToastOptions);
         },
      });
   };

   const onContactFocus = () => {
      if (!store.form.controls.contact.value) {
         store.setContactValue('+380');
      }
   };

   const onContactBlur = () => {
      if (store.form.controls.contact.value === '+380') {
         store.setContactValue('');
      }
   };

   return (
      <>
         <NavBar />
         <Container size="4" py="9">
            <Card size="2">
               <Flex direction={{ initial: 'column', md: 'row' }} gap="0">
                  <Box width={{ initial: '100%', md: '49%' }} style={{ alignSelf: 'flex-start' }}>
                     <img
                        src="/Images/employerlogin.jpg"
                        alt="candidate-register"
                        style={{
                           width: '100%',
                           height: 'clamp(420px, 48vw, 760px)',
                           borderRadius: '10px',
                           objectFit: 'cover',
                        }}
                     />
                  </Box>

                  <Box
                     width={{ initial: '100%', md: '51%' }}
                     px={{ initial: '2', md: '3' }}
                     py={{ initial: '3', md: '4' }}
                  >
                     <Flex direction="column" gap="3">
                        <Flex justify="center">
                           <img src="/Images/Logo.png" alt="logo" width="56" />
                        </Flex>

                        <Heading align="center" size="6" weight="medium">
                           {t('jobseekerRegister.jobseekAcRegistration')}
                        </Heading>

                        <form
                           onSubmit={onSubmit}
                           autoComplete="off"
                           style={{ maxWidth: 440, width: '100%', margin: '0 auto' }}
                        >
                           <Flex direction="column" gap="4">
                              <FormField
                                 label={t('jobseekerRegister.firstName')}
                                 error={store.form.controls.first_name.error}
                                 required
                              >
                                 <TextField.Root
                                    size="3"
                                    id="first_name"
                                    value={store.form.controls.first_name.value}
                                    placeholder={t('jobseekerRegister.firstName')}
                                    onChange={(e) =>
                                       store.form.controls.first_name.setValue(e.target.value)
                                    }
                                 />
                              </FormField>

                              <FormField
                                 label={t('jobseekerRegister.lastName')}
                                 error={store.form.controls.last_name.error}
                                 required
                              >
                                 <TextField.Root
                                    size="3"
                                    id="last_name"
                                    value={store.form.controls.last_name.value}
                                    placeholder={t('jobseekerRegister.lastName')}
                                    onChange={(e) =>
                                       store.form.controls.last_name.setValue(e.target.value)
                                    }
                                 />
                              </FormField>

                              <FormField
                                 label={t('jobseekerRegister.middleName')}
                                 error={store.form.controls.middle_name.error}
                              >
                                 <TextField.Root
                                    size="3"
                                    id="middle_name"
                                    value={store.form.controls.middle_name.value}
                                    placeholder={t('jobseekerRegister.middleName')}
                                    onChange={(e) =>
                                       store.form.controls.middle_name.setValue(e.target.value)
                                    }
                                 />
                              </FormField>

                              <FormField
                                 label={t('jobseekerRegister.email')}
                                 error={store.form.controls.email.error}
                              >
                                 <TextField.Root
                                    size="3"
                                    id="email"
                                    type="email"
                                    value={store.form.controls.email.value}
                                    placeholder={t('jobseekerRegister.email')}
                                    onChange={(e) =>
                                       store.form.controls.email.setValue(e.target.value)
                                    }
                                 />
                              </FormField>

                              <FormField
                                 label={t('jobseekerRegister.contactNumber')}
                                 error={store.contactError}
                              >
                                 <TextField.Root
                                    size="3"
                                    id="contact"
                                    value={store.form.controls.contact.value}
                                    placeholder={t('jobseekerRegister.contactNumber')}
                                    onFocus={onContactFocus}
                                    onBlur={onContactBlur}
                                    onChange={(e) => store.setContactValue(e.target.value)}
                                 />
                              </FormField>

                              <FormField
                                 label={t('jobseekerRegister.password')}
                                 error={store.form.controls.password.error}
                                 required
                              >
                                 <TextField.Root
                                    size="3"
                                    id="password"
                                    type={store.showPassword ? 'text' : 'password'}
                                    value={store.form.controls.password.value}
                                    placeholder={t('jobseekerRegister.password')}
                                    onChange={(e) =>
                                       store.form.controls.password.setValue(e.target.value)
                                    }
                                 >
                                    <TextField.Slot side="right">
                                       <Button
                                          type="button"
                                          size="1"
                                          variant="ghost"
                                          color="gray"
                                          aria-label={
                                             store.showPassword ? 'Hide password' : 'Show password'
                                          }
                                          onClick={store.togglePasswordVisibility}
                                       >
                                          {store.showPassword ? (
                                             <VisibilityOffIcon style={{ width: 18, height: 18 }} />
                                          ) : (
                                             <VisibilityIcon style={{ width: 18, height: 18 }} />
                                          )}
                                       </Button>
                                    </TextField.Slot>
                                 </TextField.Root>
                                 {store.form.controls.password.value && (
                                    <Box mt="1">
                                       <PasswordStrengthIndicator
                                          password={store.form.controls.password.value}
                                          translations={passwordStrengthTranslations}
                                       />
                                    </Box>
                                 )}
                              </FormField>

                              <FormField
                                 label={t('jobseekerRegister.confirmPassword')}
                                 error={store.confirmPasswordError}
                                 required
                              >
                                 <TextField.Root
                                    size="3"
                                    id="confirm_password"
                                    type={store.showConfirmPassword ? 'text' : 'password'}
                                    value={store.form.controls.confirm_password.value}
                                    placeholder={t('jobseekerRegister.confirmPassword')}
                                    onChange={(e) =>
                                       store.form.controls.confirm_password.setValue(e.target.value)
                                    }
                                 >
                                    <TextField.Slot side="right">
                                       <Button
                                          type="button"
                                          size="1"
                                          variant="ghost"
                                          color="gray"
                                          aria-label={
                                             store.showConfirmPassword
                                                ? 'Hide password'
                                                : 'Show password'
                                          }
                                          onClick={store.toggleConfirmPasswordVisibility}
                                       >
                                          {store.showConfirmPassword ? (
                                             <VisibilityOffIcon style={{ width: 18, height: 18 }} />
                                          ) : (
                                             <VisibilityIcon style={{ width: 18, height: 18 }} />
                                          )}
                                       </Button>
                                    </TextField.Slot>
                                 </TextField.Root>
                              </FormField>

                              <Box>
                                 <Box style={{ width: 304, minHeight: 78, position: 'relative' }}>
                                    {!captchaReady && <Skeleton width="304px" height="78px" />}

                                    {captchaKey && (
                                       <Box
                                          style={{
                                             position: captchaReady ? 'static' : 'absolute',
                                             inset: 0,
                                             opacity: captchaReady ? 1 : 0,
                                             pointerEvents: captchaReady ? 'auto' : 'none',
                                          }}
                                       >
                                          <ReCAPTCHA
                                             sitekey={captchaKey}
                                             hl={recaptchaLanguage}
                                             asyncScriptOnLoad={() => setCaptchaReady(true)}
                                             onErrored={() => setCaptchaReady(false)}
                                             onChange={(value) => store.setCaptchaToken(value)}
                                          />
                                       </Box>
                                    )}
                                 </Box>
                                 {store.captchaError && (
                                    <Text size="2" color="red">
                                       {store.captchaError}
                                    </Text>
                                 )}
                              </Box>

                              <Flex justify="end" align="center" wrap="wrap" gap="3">
                                 <Button
                                    size="3"
                                    type="button"
                                    variant="soft"
                                    color="gray"
                                    onClick={store.reset}
                                 >
                                    {t('jobseekerRegister.resetButton')}
                                 </Button>
                                 <Button size="3" type="submit" loading={store.submitting}>
                                    {t('jobseekerRegister.submitButton')}
                                 </Button>
                              </Flex>

                              <Text size="2" align="center" color="gray">
                                 {t('employerRegister.bottomTxt1')}{' '}
                                 <Link to="/terms-of-use">{t('employerRegister.bottomTxt2')}</Link>
                                 {', '}
                                 {t('employerRegister.bottomTxt7')}{' '}
                                 <Link to="/offer-agreement">
                                    {t('employerRegister.bottomTxt8')}
                                 </Link>{' '}
                                 {t('employerRegister.bottomTxt5')}{' '}
                                 <Link to="/data-processing-consent">
                                    {t('employerRegister.bottomTxt6')}
                                 </Link>
                              </Text>

                              <Text size="2" align="center">
                                 {t('jobseekerRegister.bottomTxt3')}{' '}
                                 <Link to="/auth/candidate-login">
                                    {t('jobseekerRegister.bottomTxt4')}
                                 </Link>
                              </Text>
                           </Flex>
                        </form>
                     </Flex>
                  </Box>
               </Flex>
            </Card>
         </Container>
         <Footer />
      </>
   );
}

export const CandidateRegister = observer(CandidateRegisterView);
